import { useState, useEffect, useMemo } from "react";
import { CircleUserRound, LineChart, Crosshair, KeyRound, Loader2, ArrowLeft, Save, Plus, X } from "lucide-react";
import { useNotification } from "../../components/Notifications/NotificationContext";
import MultiBotSelector from "../../components/BotSelector/MultiBotSelector";
import { useAuth } from "../../context/AuthProvider.jsx";

// Facebook não existe no lucide desta versão — SVG de marca inline.
function FacebookIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="#1877f2" aria-hidden="true">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
    );
}

// TikTok também não existe no lucide — SVG de marca inline.
function TikTokIcon({ size = 18 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
        </svg>
    );
}

// Campo de texto padronizado (mesmo visual do slug do RedirectForm).
function Field({ label, hint, ...props }) {
    return (
        <div>
            {label && <label className="block text-xs text-white/50 mb-1.5">{label}</label>}
            <input
                {...props}
                className="spt-input w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5 text-sm text-white outline-none placeholder:text-white/30"
            />
            {hint && <small className="block mt-1.5 text-xs text-white/40">{hint}</small>}
        </div>
    );
}

// Bloco de um serviço (Facebook / TikTok / UTMify / Tracky).
function ServiceCard({ icon, title, badge, children }) {
    return (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 space-y-3">
            <div className="flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-white/[0.04] flex items-center justify-center flex-shrink-0">{icon}</span>
                <span className="text-sm font-semibold text-white">{title}</span>
                {badge && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-[var(--brand-500)]/15 text-[var(--brand-300)] font-semibold">{badge}</span>}
            </div>
            {children}
        </div>
    );
}

// Rótulo amigável da plataforma escolhida (definida no modal de seleção).
const PLATFORM_META = {
    facebook: { label: "Facebook Pixel", icon: <FacebookIcon size={18} /> },
    tiktok: { label: "TikTok Ads", icon: <TikTokIcon size={18} /> },
    utmify: { label: "UTMify", icon: <LineChart size={18} className="text-emerald-400" /> },
};

export default function TrackingForm({ open = true, editing, platform, onBack, onSuccess, userBots }) {
    const { showError } = useNotification();
    const { user } = useAuth();
    const isAdmin = user?.is_admin === true;
    const [loading, setLoading] = useState(false);
    const sortedUserBots = useMemo(() => (Array.isArray(userBots) ? [...userBots] : []), [userBots]);

    const emptyForm = () => ({
        id: null,
        name: "",
        platform: platform || "facebook",
        facebook_pixel_id: "",
        facebook_access_token: "",
        facebook_test_event_code: "",
        tiktok_pixel_id: "",
        tiktok_access_token: "",
        tiktok_test_event_code: "",
        utmify_api_token: "",
        tracky_api_token: "",
        bot_ids: [],
        is_active: true,
    });

    const [form, setForm] = useState(emptyForm);

    const resetForm = () => setForm(emptyForm());

    useEffect(() => {
        if (editing) {
            setForm({
                id: editing.id,
                name: editing.name,
                platform: editing.platform || platform || "facebook",
                facebook_pixel_id: editing.facebook?.pixel_id || "",
                facebook_access_token: editing.facebook?.access_token || "",
                facebook_test_event_code: editing.facebook?.test_event_code || "",
                tiktok_pixel_id: editing.tiktok?.pixel_id || "",
                tiktok_access_token: editing.tiktok?.access_token || "",
                tiktok_test_event_code: editing.tiktok?.test_event_code || "",
                utmify_api_token: editing.utmify?.api_token || "",
                tracky_api_token: editing.tracky_api_token || "",
                bot_ids: editing.bots ? editing.bots.map((b) => b.id) : [],
                is_active: editing.is_active,
            });
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing, platform]);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const currentPlatform = form.platform || "facebook";
    const platformMeta = PLATFORM_META[currentPlatform] || PLATFORM_META.facebook;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim()) { showError("Nome do perfil é obrigatório."); return; }

        // Validação por plataforma (espelha o backend).
        if (currentPlatform === "facebook") {
            if (!form.facebook_pixel_id || !form.facebook_access_token) {
                showError("Configure Pixel ID e Access Token do Facebook."); return;
            }
        } else if (currentPlatform === "tiktok") {
            if (!form.tiktok_pixel_id || !form.tiktok_access_token) {
                showError("Configure Pixel ID e Access Token do TikTok."); return;
            }
        } else if (currentPlatform === "utmify") {
            if (!form.utmify_api_token) {
                showError("Configure o API Token do UTMify."); return;
            }
        }

        if (form.bot_ids.length === 0) { showError("Selecione pelo menos um bot."); return; }

        setLoading(true);
        try {
            await onSuccess(form);
            resetForm();
        } catch (err) {
            // erro tratado no parent
        } finally {
            setLoading(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-2 md:p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onBack} />
            <div
                className="relative w-full max-w-2xl rounded-2xl border border-white/[0.09] bg-[#10141a] p-4 md:p-6 shadow-[0_24px_64px_rgba(0,0,0,0.55)] text-white"
                style={{ maxHeight: "92vh", overflowY: "auto", colorScheme: "dark" }}
            >
                {/* Header */}
                <div className="flex items-start justify-between mb-6 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-[rgba(139,92,246,0.12)] border border-[rgba(139,92,246,0.18)] flex items-center justify-center">
                            {platformMeta.icon}
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-lg font-semibold">{editing ? "Editar Perfil de Tracking" : "Novo Perfil de Tracking"}</h2>
                            <p className="text-sm text-white/50">Configure {platformMeta.label} e associe aos seus bots.</p>
                        </div>
                    </div>
                    <button onClick={onBack} className="action-btn flex-shrink-0" aria-label="Fechar">
                        <X size={18} />
                    </button>
                </div>

                <form
                    onSubmit={handleSubmit}
                    className="space-y-6"
                    style={{ opacity: loading ? 0.6 : 1, pointerEvents: loading ? "none" : "auto" }}
                >
                    {/* Perfil Ativo */}
                    <button
                        type="button"
                        onClick={() => set({ is_active: !form.is_active })}
                        className={`w-full flex items-center gap-3 p-4 rounded-xl border text-left transition-all ${form.is_active
                            ? "bg-[#4c1d95] border-[#6d28d9]"
                            : "bg-white/[0.02] border-white/[0.06] hover:border-white/[0.1] hover:bg-white/[0.04]"}`}
                    >
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${form.is_active ? "bg-white/10 text-white" : "bg-white/[0.04] text-white/40"}`}>
                            <CircleUserRound size={20} />
                        </div>
                        <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-white">Perfil Ativo</div>
                            <div className="text-xs text-white/50">Quando desativado, o perfil para de enviar eventos de rastreamento.</div>
                        </div>
                        <span className={`relative w-10 h-6 rounded-full border transition-colors flex-shrink-0 ${form.is_active ? "bg-[var(--brand-500)] border-[var(--brand-400)]" : "bg-white/10 border-white/20"}`}>
                            <span className={`absolute top-[3px] w-4 h-4 rounded-full bg-white shadow-sm transition-all ${form.is_active ? "left-[19px]" : "left-[3px]"}`} />
                        </span>
                    </button>

                    {/* Nome */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Nome do Perfil <span className="text-[var(--brand-400)]">*</span>
                        </label>
                        <Field
                            value={form.name}
                            onChange={(e) => set({ name: e.target.value })}
                            required
                            placeholder="Ex: Campanha Black Friday"
                            hint="Nome do perfil para identificação interna."
                        />
                    </div>

                    {/* Bots */}
                    <div>
                        <label className="block text-sm font-semibold text-white mb-2">
                            Selecionar Bots <span className="text-[var(--brand-400)]">*</span>
                        </label>
                        {sortedUserBots.length === 0 ? (
                            <p className="text-white/40 text-sm">Você ainda não possui bots cadastrados.</p>
                        ) : (
                            <MultiBotSelector
                                bots={sortedUserBots}
                                selected={form.bot_ids}
                                onChange={(ids) => set({ bot_ids: ids })}
                            />
                        )}
                    </div>

                    {/* Serviços */}
                    <div className="space-y-3">
                        {currentPlatform === "facebook" && (
                            <ServiceCard icon={<FacebookIcon size={18} />} title="Facebook Pixel">
                                <Field label="Pixel ID" value={form.facebook_pixel_id}
                                    onChange={(e) => set({ facebook_pixel_id: e.target.value })}
                                    placeholder="123456789012345" />
                                <Field label="Access Token" type="password" value={form.facebook_access_token}
                                    onChange={(e) => set({ facebook_access_token: e.target.value })}
                                    placeholder="EAAxxxx..." />
                                <Field label="Test Event Code (opcional)" value={form.facebook_test_event_code}
                                    onChange={(e) => set({ facebook_test_event_code: e.target.value })}
                                    placeholder="TESTxxxx" />
                            </ServiceCard>
                        )}

                        {currentPlatform === "tiktok" && (
                            <ServiceCard icon={<TikTokIcon size={18} />} title="TikTok Ads">
                                <Field label="Pixel ID" value={form.tiktok_pixel_id}
                                    onChange={(e) => set({ tiktok_pixel_id: e.target.value })}
                                    placeholder="C1XXXXXXXXXXXXXXXXXX" />
                                <Field label="Access Token" type="password" value={form.tiktok_access_token}
                                    onChange={(e) => set({ tiktok_access_token: e.target.value })}
                                    placeholder="ctoken_xxxxxxxxx" />
                                <Field label="Test Event Code (opcional)" value={form.tiktok_test_event_code}
                                    onChange={(e) => set({ tiktok_test_event_code: e.target.value })}
                                    placeholder="TESTxxxx" />
                            </ServiceCard>
                        )}

                        <ServiceCard icon={<LineChart size={18} className="text-emerald-400" />} title="UTMify">
                            <Field label="API Token" type="password" value={form.utmify_api_token}
                                onChange={(e) => set({ utmify_api_token: e.target.value })}
                                placeholder="seu-token-utmify" />
                        </ServiceCard>

                        {isAdmin && (
                            <ServiceCard icon={<Crosshair size={18} className="text-indigo-400" />} title="Tracky" badge="admin">
                                <Field label="API Token" type="password" value={form.tracky_api_token}
                                    onChange={(e) => set({ tracky_api_token: e.target.value })}
                                    placeholder="seu-token-tracky"
                                    hint="Plataforma de trackeamento compatível com a UTMify. Visível apenas para administradores." />
                            </ServiceCard>
                        )}
                    </div>

                    <div className="rounded-lg bg-white/[0.02] border border-white/[0.06] px-3 py-2.5 flex items-center gap-2 text-xs text-white/50">
                        <KeyRound size={14} className="text-white/40 flex-shrink-0" />
                        Preencha os campos obrigatórios da plataforma <strong className="text-white/70">{platformMeta.label}</strong>.
                    </div>

                    {/* Ações */}
                    <div className="flex gap-3 justify-end pt-2">
                        <button type="button" onClick={onBack} className="btn-secondary flex items-center gap-2">
                            <ArrowLeft size={15} /> Voltar
                        </button>
                        <button disabled={loading} className="btn-primary flex items-center gap-2">
                            {loading ? (
                                <><Loader2 size={15} className="animate-spin" /> Salvando...</>
                            ) : (
                                <>{editing ? <Save size={15} /> : <Plus size={15} />} {editing ? "Atualizar" : "Criar"}</>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
